import { FunctionComponent } from "react";

import { DATE_FORMATS, DateTime } from "@bps/date-time";
import {
  DetailsList,
  FontIcon,
  FontSizes,
  IColumn,
  IconButton,
  NoDataTile,
  Text,
  Toggle,
  TooltipHost,
  useTheme
} from "@bps/fluent-ui";
import { TenantNetworkAuthenticationSettingsDto } from "@libs/api/gateways/plt/plt-gateway.dtos";

const ADD_RANGE_TEXT = "Add Ip Range";
export interface IpRangeTableProps {
  setShowAddIpRangeDialog: (
    setting: TenantNetworkAuthenticationSettingsDto | null
  ) => void;
  hasWritePermission: boolean;
  networkSettings: TenantNetworkAuthenticationSettingsDto[] | undefined;
  onRemoveIpRange: (item: TenantNetworkAuthenticationSettingsDto) => void;
  onToggleIpRange: (item: TenantNetworkAuthenticationSettingsDto) => void;
}

export const NetworkAuthenticationSettingsTable: FunctionComponent<IpRangeTableProps> = ({
  setShowAddIpRangeDialog,
  hasWritePermission,
  networkSettings = [],
  onRemoveIpRange,
  onToggleIpRange
}) => {
  const theme = useTheme();
  const ipRangeTableColumns: IColumn[] = [
    {
      key: "ipRangeBegin",
      name: "IpRange Begin",
      minWidth: 120,
      maxWidth: 120,
      onRender: (item: TenantNetworkAuthenticationSettingsDto) => (
        <Text>{item.startAddress}</Text>
      )
    },
    {
      key: "ipRangeEnd",
      name: "IpRange End",
      minWidth: 120,
      maxWidth: 120,
      onRender: (item: TenantNetworkAuthenticationSettingsDto) => (
        <Text>{item.endAddress}</Text>
      )
    },
    {
      key: "description",
      name: "Description",
      minWidth: 120,
      maxWidth: 120,
      onRender: (item: TenantNetworkAuthenticationSettingsDto) => (
        <Text>{item.description}</Text>
      )
    },
    {
      key: "updatedDate",
      name: "Last Updated Date",
      minWidth: 200,
      maxWidth: 200,
      onRender: (item: TenantNetworkAuthenticationSettingsDto) => {
        const updatedDate =
          item.changeLog.updatedDate ?? item.changeLog.createdDate;
        return (
          <Text>
            {updatedDate
              ? DateTime.fromISO(updatedDate).toFormat(
                  DATE_FORMATS.DAY_DEFAULT_FORMAT
                )
              : undefined}
          </Text>
        );
      }
    },
    {
      key: "updatedBy",
      name: "Last Updated By",
      minWidth: 250,
      maxWidth: 250,
      onRender: (item: TenantNetworkAuthenticationSettingsDto) => (
        <Text>{item.changeLog.updatedBy ?? item.changeLog.createdBy}</Text>
      )
    },
    {
      key: "activate",
      name: "Enabled",
      minWidth: 70,
      maxWidth: 70,
      onRender: (item: TenantNetworkAuthenticationSettingsDto) =>
        hasWritePermission ? (
          <TooltipHost content={item.isActive ? "Disable" : "Enable"}>
            <Toggle
              checked={item.isActive}
              onChange={() => {
                onToggleIpRange(item);
              }}
              styles={{ root: { margin: 0 } }}
            />
          </TooltipHost>
        ) : null
    },
    {
      key: "edit",
      name: "",
      minWidth: 32,
      maxWidth: 32,
      onRender: (item: TenantNetworkAuthenticationSettingsDto) =>
        !item.isSystemManaged ? (
          <IconButton
            iconProps={{ iconName: "Edit" }}
            styles={{ root: { justifySelf: "end", alignSelf: "start" } }}
            onClick={() => {
              setShowAddIpRangeDialog(item);
            }}
          />
        ) : null
    },
    {
      key: "delete",
      name: "",
      minWidth: 32,
      maxWidth: 32,
      onRenderHeader: () =>
        hasWritePermission ? (
          <TooltipHost content={ADD_RANGE_TEXT}>
            <IconButton
              iconProps={{ iconName: "Add" }}
              styles={{ root: { justifySelf: "end", alignSelf: "start" } }}
              onClick={() => setShowAddIpRangeDialog(null)}
            />
          </TooltipHost>
        ) : null,
      onRender: (item: TenantNetworkAuthenticationSettingsDto) => {
        if (item.isSystemManaged)
          return (
            <TooltipHost content="System managed">
              <FontIcon
                iconName="System"
                styles={{
                  root: {
                    color: theme.semanticColors.errorIcon,
                    fontSize: FontSizes.size24
                  }
                }}
              />
            </TooltipHost>
          );

        return hasWritePermission ? (
          <IconButton
            iconProps={{ iconName: "Delete" }}
            styles={{ root: { justifySelf: "end", alignSelf: "start" } }}
            onClick={() => {
              onRemoveIpRange(item);
            }}
          />
        ) : null;
      }
    }
  ];
  return !networkSettings?.length ? (
    <NoDataTile
      showBoxShadow={false}
      styles={{ root: { width: "100%" } }}
      textProps={{ text: "There are no IP Ranges configured" }}
      linkProps={{
        onClick: () => setShowAddIpRangeDialog(null),
        text: ADD_RANGE_TEXT,
        hidden: !hasWritePermission
      }}
    />
  ) : (
    <DetailsList items={networkSettings} columns={ipRangeTableColumns} />
  );
};
